"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Shield, Lock, FileText, UserCheck, Mail, Phone, MapPin, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const policySections = [{
  icon: Shield,
  title: "1. Information we collect",
  content: "When you reserve a vehicle or request a quote with SANTA LIMO, we collect only the necessary details required to deliver reliable chauffeur service. This includes passenger names, telephone numbers, email addresses, pickup and drop-off coordinates, flight numbers for airport monitoring, and specific trip preferences."
}, {
  icon: Lock,
  title: "2. Payment security and encryption",
  content: "All financial transactions are processed through level-1 PCI-DSS compliant payment gateways with 256-bit SSL encryption. We never store complete credit card numbers, CVV security codes, or banking credentials on our local servers or dispatch terminals."
}, {
  icon: UserCheck,
  title: "3. Non-disclosure and data sharing",
  content: "We do not sell, rent, trade, or monetize your personal information to marketing aggregators or third-party advertisers. Chauffeurs receive only the essential itinerary details required to complete your pickup and transfer smoothly. Flight data is utilized strictly for real-time schedule tracking."
}, {
  icon: FileText,
  title: "4. Driver applications and document retention",
  content: "Information submitted through our driver application portal—including motor vehicle records, commercial driver licenses, and background verifications—is held in strict confidentiality. These records are retained solely in compliance with Illinois Department of Transportation and City of Chicago licensing regulations."
}, {
  icon: CheckCircle2,
  title: "5. Your rights and data preferences",
  content: "You have the right to request a copy of your stored profile information, update outdated contact records, or ask for the deletion of non-essential profile data at any time. Legally required dispatch records are maintained according to statutory transportation requirements."
}];
const highlights = ["256-bit SSL encrypted transactions", "No data sharing with third-party advertisers", "Real-time flight data used solely for dispatch", "Strict adherence to Illinois DOT regulations"];
export default function Privacy() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="our-privacy-policy" data-nav="dark" className="dark bg-background text-foreground py-16 md:py-24 px-4 sm:px-6 lg:px-8 border-b border-border/40">
      <div className="max-w-[800px] mx-auto">
        {/* Header Block */}
        <motion.div initial={shouldReduceMotion ? false : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/30 border border-primary/30 text-primary text-xs font-semibold tracking-wider uppercase mb-4">
            <Shield className="size-3.5 text-primary" />
            <span>Passenger Privacy & Security</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-default font-extrabold tracking-tight text-foreground mb-4">
            Our privacy policy
          </h1>

          <p className="text-base sm:text-lg text-muted-foreground font-default leading-relaxed max-w-xl mx-auto">
            How we protect your personal information, trip details, and payment data across our Chicago and regional chauffeur operations.
          </p>

          <p className="text-xs text-muted-foreground/80 mt-3 font-mono">
            Last revised: October 2024 • SANTA LIMO Compliance Office
          </p>
        </motion.div>

        {/* Key Guarantees Card */}
        <motion.div initial={shouldReduceMotion ? false : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.45,
        ease: "easeOut",
        delay: 0.1
      }} className="rounded-xl border border-border bg-card p-6 md:p-8 mb-12 shadow-sm relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary/60 to-transparent" />
          <h2 className="text-lg font-default font-bold text-card-foreground mb-4 flex items-center gap-2">
            <Shield className="size-5 text-primary shrink-0" />
            <span>Core privacy commitments</span>
          </h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-card-foreground/90 font-default">
            {highlights.map((item, i) => <li key={i} data-index={i} className="flex items-start gap-2.5">
                <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>)}
          </ul>
        </motion.div>

        {/* Structured Policy Clauses */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="space-y-10">
          {policySections.map((section, idx) => {
          const IconComponent = section.icon;
          return <motion.article key={section.title} data-index={idx} variants={itemVariants} className="group border-b border-border/60 pb-8 last:border-b-0 last:pb-0">
                <div className="flex items-start gap-3 mb-3">
                  <div className="p-2 rounded-md bg-muted border border-border/80 text-primary shrink-0 mt-0.5">
                    <IconComponent className="size-4" />
                  </div>
                  <h2 className="text-xl font-default font-bold text-foreground tracking-tight">
                    {section.title}
                  </h2>
                </div>
                <p className="text-muted-foreground font-default leading-relaxed text-sm sm:text-base pl-11">
                  {section.content}
                </p>
              </motion.article>;
        })}
        </motion.div>

        {/* Contact & Inquiry Container */}
        <motion.div initial={shouldReduceMotion ? false : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.45,
        ease: "easeOut"
      }} className="mt-14 rounded-xl border border-border bg-card p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="space-y-2">
              <h2 className="text-lg font-default font-bold text-card-foreground">
                Privacy questions or data requests
              </h2>
              <p className="text-sm text-muted-foreground font-default max-w-md">
                Reach our compliance desk for questions about your profile, booking records, or data retention policies.
              </p>
              <div className="pt-2 flex flex-col sm:flex-row sm:items-center gap-4 text-xs font-default text-muted-foreground">
                <a href="mailto:contact@santalimo.com" className="inline-flex items-center gap-1.5 hover:text-primary transition-colors text-foreground">
                  <Mail className="size-3.5 text-primary" />
                  <span>contact@santalimo.com</span>
                </a>
                <a href="tel:3125550100" className="inline-flex items-center gap-1.5 hover:text-primary transition-colors text-foreground">
                  <Phone className="size-3.5 text-primary" />
                  <span>(312) 555-0100</span>
                </a>
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="size-3.5 text-primary" />
                  <span>Chicago, Illinois, USA</span>
                </span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row md:flex-col gap-2 shrink-0">
              <Button asChild variant="default" size="sm">
                <Link to="/ContactUsPage">Contact support</Link>
              </Button>
              <Button asChild variant="outline" size="sm">
                <Link to="/HomePage">Return to home</Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}